/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import MarkdownFigure from '@/components/charts/MarkdownFigure';
import { Check, Copy, FileCode2 } from 'lucide-react';
import { useEffect, useState, type RefObject } from 'react';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import library functions */
import { createTimestampedFilename, downloadTextFile } from '@/lib/presentation/fileDownload';

interface StaticMarkdownProps {
    className?: string;
    content: string;
    containerRef?: RefObject<HTMLDivElement | null>;
    fillParent?: boolean;
}

function StaticMarkdown({ content, fillParent = false, ...props }: StaticMarkdownProps) {
    const { t } = useI18n();
    const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');

    useEffect(() => {
        if (copyState !== 'copied') {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setCopyState('idle');
        }, 1500);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [copyState]);

    async function copyMarkdownSource(): Promise<void> {
        await navigator.clipboard.writeText(content);
        setCopyState('copied');
    }

    function downloadMarkdownSource(): void {
        downloadTextFile(
            content,
            createTimestampedFilename('markdown-document', 'md'),
            'text/markdown',
        );
    }

    return (
        <div className={`relative w-full${fillParent ? ' h-full min-h-0' : ''}`}>
            <div className="action-controls absolute right-3 top-3 z-10 flex gap-1 rounded-box p-1">
                <div className="tooltip tooltip-left" data-tip={t(copyState === 'copied' ? 'Copied' : 'Copy Markdown source')}>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-square"
                        aria-label={t(copyState === 'copied' ? 'Copied' : 'Copy Markdown source')}
                        onClick={() => {
                            void copyMarkdownSource();
                        }}
                    >
                        {copyState === 'copied'
                            ? <Check className="h-4 w-4" aria-hidden="true" />
                            : <Copy className="h-4 w-4" aria-hidden="true" />}
                    </button>
                </div>
                <div className="tooltip tooltip-left" data-tip={t('Download Markdown source')}>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-square"
                        aria-label={t('Download Markdown source')}
                        onClick={downloadMarkdownSource}
                    >
                        <FileCode2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>
            </div>
            <MarkdownFigure {...props} content={content} fillParent={fillParent} />
        </div>
    );
}

export default StaticMarkdown;
